import { useEffect, useMemo, useState } from "react";
import {
  CaretDown,
  Check,
  CurrencyDollar,
  Eye,
  EyeSlash,
  FileText,
  PaperPlaneTilt,
  ArrowsClockwise,
  Buildings,
  Pencil,
  Plus,
  Receipt,
  TrendDown,
  TrendUp,
  X,
} from "@phosphor-icons/react";

import { useFinancialsVisible } from "../../../hooks/useFinancialsVisible";
import { fmtCurrency } from "./formatters";
import { generateQuote, sendQuote, checkQuoteStatus, getProjectFinancialSummary, requestPo, sendInvoice } from "../../../api/projects";

function QuickAddCostRow({ newLabel, newAmount, saving, onLabel, onAmount, onSave, onCancel }) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="quick-add-cost-row">
      <input
        autoFocus
        className="flex-1 text-sm border border-slate-200 rounded px-2 py-1 outline-none focus:border-primary bg-white"
        placeholder="Description (e.g. Materials)"
        value={newLabel}
        onChange={(e) => onLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
      />
      <div className="relative w-28 flex-shrink-0">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
        <input
          type="number"
          step="0.01"
          min="0"
          className="w-full text-sm border border-slate-200 rounded pl-5 pr-2 py-1 outline-none focus:border-primary bg-white"
          placeholder="0.00"
          value={newAmount}
          onChange={(e) => onAmount(e.target.value)}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        />
      </div>
      <button
        onClick={onSave}
        disabled={saving || !newLabel.trim() || !newAmount}
        data-testid="quick-add-cost-save"
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
      >
        <Check size={12} weight="bold" /> Add
      </button>
      <button onClick={onCancel} className="text-slate-400 hover:text-slate-700 transition-colors" title="Cancel">
        <X size={14} />
      </button>
    </div>
  );
}

function QuoteActions({ project, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const quoteStatus = project.xero_quote_status;
  const quotedPrice = project.quoted_price;
  const hasPrice = quotedPrice != null && quotedPrice !== "" && Number(quotedPrice) > 0;

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await generateQuote(project.id);
      // Brief delay to let the fire-and-forget background task complete
      await new Promise((r) => setTimeout(r, 2000));
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Quote generation failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      await sendQuote(project.id);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Send quote failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    setLoading(true);
    try {
      await checkQuoteStatus(project.id);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Check quote status failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      await generateQuote(project.id);
      // Brief delay to let the fire-and-forget background task complete
      await new Promise((r) => setTimeout(r, 2000));
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Quote regeneration failed:", e);
    } finally {
      setLoading(false);
    }
  };

  // No quoted price set — nothing to show
  if (!hasPrice && !quoteStatus) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100" data-testid="quote-actions">
      <FileText size={14} className="text-slate-400" weight="fill" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Quote</span>

      {/* No quote yet, price is set */}
      {!quoteStatus && hasPrice && (
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="ml-2 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
        >
          <FileText size={12} weight="bold" /> Generate Quote
        </button>
      )}

      {/* DRAFT */}
      {quoteStatus === "DRAFT" && (
        <>
          <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
            Draft
          </span>
          <button
            onClick={handleSend}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            <PaperPlaneTilt size={12} weight="bold" /> Send Quote
          </button>
          <button
            onClick={handleCheckStatus}
            disabled={loading}
            className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ArrowsClockwise size={11} /> Check Status
          </button>
        </>
      )}

      {/* SENT */}
      {quoteStatus === "SENT" && (
        <>
          <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
            Awaiting Response
          </span>
          <button
            onClick={handleCheckStatus}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            <ArrowsClockwise size={12} /> Check Status
          </button>
        </>
      )}

      {/* ACCEPTED */}
      {quoteStatus === "ACCEPTED" && (
        <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
          <Check size={11} weight="bold" /> Quote Accepted
        </span>
      )}

      {/* DECLINED */}
      {quoteStatus === "DECLINED" && (
        <>
          <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
            Quote Declined
          </span>
          <button
            onClick={handleRegenerate}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            <ArrowsClockwise size={12} /> Regenerate
          </button>
        </>
      )}

      {/* INVOICED */}
      {quoteStatus === "INVOICED" && (
        <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
          Converted to Invoice
        </span>
      )}

      {/* Error */}
      {quoteStatus === "error" && (
        <>
          <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
            Sync Error
          </span>
          <button
            onClick={handleRegenerate}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            <ArrowsClockwise size={12} /> Retry
          </button>
        </>
      )}
    </div>
  );
}

function PoActions({ project, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const poStatus = project.po_request_status;
  const invoiced = project.final_invoice_amount;
  const hasInvoiced = invoiced != null && invoiced !== "" && Number(invoiced) > 0;
  const hasClientEmail = (project.client_emails || []).length > 0;

  const handleRequestPo = async (mode) => {
    setLoading(true);
    setMenuOpen(false);
    try {
      await requestPo(project.id, mode);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("PO request failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    setLoading(true);
    try {
      await sendInvoice(project.id);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Send invoice failed:", e);
    } finally {
      setLoading(false);
    }
  };

  // Nothing to show if no client email configured
  if (!hasClientEmail) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100" data-testid="po-actions">
      <Receipt size={14} className="text-slate-400" weight="fill" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">PO &amp; Invoice</span>

      {/* PO Status badges */}
      {poStatus === "requested" && (
        <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
          PO Requested
        </span>
      )}
      {poStatus === "confirmed" && (
        <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
          <Check size={11} weight="bold" /> PO Confirmed
        </span>
      )}

      {/* Request PO dropdown */}
      {poStatus !== "confirmed" && (
        <div className="relative ml-2">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            <PaperPlaneTilt size={12} weight="bold" /> Request PO <CaretDown size={10} />
          </button>
          {menuOpen && (
            <div className="absolute z-20 top-full left-0 mt-1 w-56 bg-white border border-slate-200 rounded shadow-lg py-1">
              <button
                onClick={() => handleRequestPo("send")}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold">Send email requesting PO</span>
                <span className="block text-slate-400 text-[10px]">Default — sends immediately</span>
              </button>
              <button
                onClick={() => handleRequestPo("draft")}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold">Create draft in Outlook</span>
                <span className="block text-slate-400 text-[10px]">Review before sending</span>
              </button>
              <button
                onClick={() => handleRequestPo("send_and_auto_invoice")}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold">Send + auto-invoice on PO</span>
                <span className="block text-slate-400 text-[10px]">Automatically invoices when PO received</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Send Invoice button */}
      {hasInvoiced && (
        <button
          onClick={handleSendInvoice}
          disabled={loading}
          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
        >
          <Receipt size={12} weight="bold" /> Send Invoice
        </button>
      )}
    </div>
  );
}

function FinancialSummary({ project, onSave, onRefresh }) {
  const { hidden, toggle } = useFinancialsVisible();
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [subJobCount, setSubJobCount] = useState(null);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    getProjectFinancialSummary(project.id)
      .then((data) => {
        if (!cancelled && data.has_sub_jobs) {
          setSubJobCount(data.sub_job_count);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [project?.id]);

  const quoted = project.quoted_price;
  const invoiced = project.final_invoice_amount;
  const hasQuoted = quoted != null && quoted !== "";
  const hasInvoiced = invoiced != null && invoiced !== "";

  let variance = null;
  let variancePct = null;
  if (hasQuoted && hasInvoiced) {
    variance = Number(invoiced) - Number(quoted);
    variancePct = Number(quoted) === 0 ? null : (variance / Number(quoted)) * 100;
  }
  const masked = <span className="tracking-widest text-slate-400 select-none">••••••</span>;

  const startEdit = (field, currentValue) => {
    setEditing(field);
    setEditVal(currentValue == null || currentValue === "" ? "" : String(currentValue));
  };

  const commitEdit = async () => {
    if (!editing) return;
    setSaving(true);
    const raw = editVal.trim().replace(/[^0-9.]/g, "");
    const value = raw === "" ? null : parseFloat(raw);
    try {
      await onSave(editing, value);
    } finally {
      setSaving(false);
      setEditing(null);
    }
  };

  const cancelEdit = () => setEditing(null);

  const EditableTile = ({ field, label, hasVal, formattedVal, placeholder, testId }) => {
    if (editing === field) {
      return (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 text-sm">$</span>
            <input
              autoFocus
              type="number"
              step="0.01"
              min="0"
              className="font-cabinet text-xl font-black w-full bg-slate-50 border border-slate-200 rounded px-2 py-0.5 outline-none focus:border-primary"
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onWheel={(e) => e.target.blur()}
              onBlur={commitEdit}
              onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") cancelEdit(); }}
              disabled={saving}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className="space-y-1 group cursor-pointer rounded hover:bg-slate-50 transition-colors -mx-1 px-1 py-1"
        data-testid={testId}
        onClick={() => !hidden && startEdit(field, field === "quoted_price" ? quoted : invoiced)}
        title="Click to edit"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          {label}
          {!hidden && <Pencil size={9} className="opacity-0 group-hover:opacity-50 transition-opacity" />}
        </p>
        <p className="font-cabinet text-2xl font-black tracking-tight text-slate-900">
          {hasVal ? (hidden ? masked : formattedVal) : <span className="text-slate-300 text-base">{placeholder}</span>}
        </p>
      </div>
    );
  };

  return (
    <section data-testid="financial-summary-section" className="bg-white border border-slate-200 rounded-sm p-5">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CurrencyDollar size={16} className="text-emerald-500" weight="fill" />
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Financial Summary</h3>
        </div>
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700 transition-colors"
          title={hidden ? "Show amounts" : "Hide amounts"}
          data-testid="toggle-financials-visibility"
        >
          {hidden ? <EyeSlash size={13} weight="bold" /> : <Eye size={13} weight="bold" />}
          <span className="hidden sm:inline">{hidden ? "Show" : "Hide"}</span>
        </button>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <EditableTile field="quoted_price" label="Quoted Price" hasVal={hasQuoted} formattedVal={fmtCurrency(quoted)} placeholder="Not quoted yet" testId="quoted-price-tile" />
        <EditableTile field="final_invoice_amount" label="Final Invoice" hasVal={hasInvoiced} formattedVal={fmtCurrency(invoiced)} placeholder="Not invoiced yet" testId="final-invoice-tile" />
        <div className="space-y-1" data-testid="variance-tile">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Variance</p>
          {variance == null ? (
            <p className="font-cabinet text-2xl font-black tracking-tight text-slate-300">—</p>
          ) : hidden ? (
            <p className="font-cabinet text-2xl font-black tracking-tight">{masked}</p>
          ) : (
            <div className="flex items-center gap-2">
              {variance > 0 ? <TrendUp size={20} className="text-emerald-500" weight="bold" /> : variance < 0 ? <TrendDown size={20} className="text-rose-500" weight="bold" /> : null}
              <p className={`font-cabinet text-2xl font-black tracking-tight ${variance > 0 ? "text-emerald-600" : variance < 0 ? "text-rose-600" : "text-slate-900"}`}>
                {variance > 0 ? "+" : ""}{fmtCurrency(variance)}
              </p>
            </div>
          )}
          {variancePct != null && !hidden && (
            <p className={`text-xs ${variancePct > 0 ? "text-emerald-600" : variancePct < 0 ? "text-rose-600" : "text-slate-400"}`}>
              {variancePct > 0 ? "+" : ""}{variancePct.toFixed(1)}% vs quote
            </p>
          )}
        </div>
      </div>
      {subJobCount != null && (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="sub-job-totals-indicator">
          <Buildings size={12} className="text-slate-400" />
          Totals from {subJobCount} sub-job{subJobCount !== 1 ? "s" : ""}
        </p>
      )}
      <QuoteActions project={project} onRefresh={onRefresh} />
      <PoActions project={project} onRefresh={onRefresh} />
    </section>
  );
}

const TAG_PALETTE = [
  { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700" },
  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700" },
  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
  { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700" },
  { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700" },
  { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700" },
];

function tagColor(tag, allTags) {
  if (!tag) return null;
  const index = allTags.indexOf(tag);
  return TAG_PALETTE[index % TAG_PALETTE.length];
}

function CostsCard({ project, onSave }) {
  const { hidden } = useFinancialsVisible();
  const costs = project.costs || [];
  const total = costs.reduce((sum, cost) => sum + (Number.isFinite(Number(cost.amount)) ? Number(cost.amount) : 0), 0);
  const invoiced = project.final_invoice_amount;
  const hasInvoiced = invoiced != null && invoiced !== "";
  const profit = hasInvoiced ? Number(invoiced) - total : null;
  const masked = <span className="tracking-widest text-slate-400 select-none">••••••</span>;

  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const handleAddCost = async () => {
    const amount = parseFloat(newAmount);
    if (!newLabel.trim() || !Number.isFinite(amount)) return;
    setAddSaving(true);
    try {
      await onSave("costs", [...costs, { label: newLabel.trim(), amount, notes: "", tag: "" }]);
      setNewLabel("");
      setNewAmount("");
      setShowAdd(false);
    } finally {
      setAddSaving(false);
    }
  };

  const groups = useMemo(() => {
    const groupMap = new Map();
    const untagged = { tag: "Untagged", isUntagged: true, items: [], amount: 0 };
    for (const cost of costs) {
      const amount = Number.isFinite(Number(cost.amount)) ? Number(cost.amount) : 0;
      const tag = (cost.tag || "").trim();
      if (!tag) {
        untagged.items.push(cost);
        untagged.amount += amount;
        continue;
      }
      if (!groupMap.has(tag)) groupMap.set(tag, { tag, items: [], amount: 0 });
      const group = groupMap.get(tag);
      group.items.push(cost);
      group.amount += amount;
    }
    const ordered = Array.from(groupMap.values());
    if (untagged.items.length > 0) ordered.push(untagged);
    return ordered;
  }, [costs]);

  const uniqueTags = useMemo(
    () => groups.filter((group) => !group.isUntagged).map((group) => group.tag),
    [groups],
  );

  const toggleCollapsed = (tag) => setCollapsed((prev) => ({ ...prev, [tag]: !prev[tag] }));

  if (costs.length === 0) {
    return (
      <section data-testid="costs-section" className="bg-white border border-slate-200 rounded-sm p-5">
        <header className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Receipt size={14} className="text-slate-500" weight="fill" />
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Cost Breakdown</h3>
          </div>
          <button onClick={() => setShowAdd((value) => !value)} data-testid="add-cost-btn" className="flex items-center gap-1 text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors">
            <Plus size={11} weight="bold" /> Add Item
          </button>
        </header>
        {showAdd ? (
          <QuickAddCostRow
            newLabel={newLabel}
            newAmount={newAmount}
            saving={addSaving}
            onLabel={setNewLabel}
            onAmount={setNewAmount}
            onSave={handleAddCost}
            onCancel={() => { setShowAdd(false); setNewLabel(""); setNewAmount(""); }}
          />
        ) : (
          <p className="text-xs text-slate-400 italic">No cost items recorded. Click "Add Item" above to add materials, labour, subcontractors…</p>
        )}
      </section>
    );
  }

  const hasAnyTag = uniqueTags.length > 0;
  const allCollapsed = hasAnyTag && groups.every((group) => collapsed[group.tag]);
  const setAll = (state) => {
    const next = {};
    for (const group of groups) next[group.tag] = state;
    setCollapsed(next);
  };

  return (
    <section data-testid="costs-section" className="bg-white border border-slate-200 rounded-sm p-5">
      <header className="flex items-center gap-2 mb-3">
        <Receipt size={14} className="text-slate-500" weight="fill" />
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Cost Breakdown</h3>
        <span className="text-[10px] text-slate-400 ml-1">({costs.length} item{costs.length === 1 ? "" : "s"})</span>
        {hasAnyTag && (
          <button
            type="button"
            onClick={() => setAll(!allCollapsed)}
            className="ml-auto text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700 transition-colors"
            data-testid="toggle-all-cost-groups"
            title={allCollapsed ? "Expand all categories" : "Collapse all categories"}
          >
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        )}
        <button onClick={() => setShowAdd((value) => !value)} data-testid="add-cost-btn" className={`${hasAnyTag ? "" : "ml-auto"} flex items-center gap-1 text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors`}>
          <Plus size={11} weight="bold" /> Add Item
        </button>
      </header>

      <div className="border border-slate-100 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2">Label</th>
              {!hasAnyTag && <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2 w-28">Tag</th>}
              <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2">Notes</th>
              <th className="text-right text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2 w-32">Amount</th>
            </tr>
          </thead>

          {!hasAnyTag && (
            <tbody>
              {costs.map((cost, index) => (
                <tr key={index} className="border-t border-slate-100" data-testid={`cost-line-${index}`}>
                  <td className="px-3 py-2 text-slate-800">{cost.label || <span className="text-slate-300 italic">(no label)</span>}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{cost.tag || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{cost.notes || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-right text-slate-800 font-medium tabular-nums">{hidden ? masked : fmtCurrency(cost.amount)}</td>
                </tr>
              ))}
            </tbody>
          )}

          {hasAnyTag && groups.map((group) => {
            const colour = group.isUntagged ? null : tagColor(group.tag, uniqueTags);
            const isCollapsed = !!collapsed[group.tag];
            return (
              <tbody key={group.tag} data-testid={`cost-group-${group.tag}`}>
                <tr className={`border-t-2 border-slate-200 cursor-pointer hover:bg-slate-50 ${colour ? `${colour.bg}/40` : "bg-slate-50/40"}`} onClick={() => toggleCollapsed(group.tag)} data-testid={`cost-group-header-${group.tag}`}>
                  <td colSpan={2} className="px-3 py-2 select-none">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                      <CaretDown size={11} className={`transition-transform ${isCollapsed ? "-rotate-90" : "rotate-0"}`} />
                      {group.isUntagged ? (
                        <span className="text-slate-500">Untagged</span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border ${colour.border} ${colour.bg} ${colour.text}`}>{group.tag}</span>
                      )}
                      <span className="text-slate-400">({group.items.length})</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-xs font-semibold text-slate-600 tabular-nums">{hidden ? masked : fmtCurrency(group.amount)}</td>
                </tr>
                {!isCollapsed && group.items.map((cost, index) => (
                  <tr key={`${group.tag}-${index}`} className="border-t border-slate-100" data-testid={`cost-line-${group.tag}-${index}`}>
                    <td className="px-3 py-2 text-slate-800">{cost.label || <span className="text-slate-300 italic">(no label)</span>}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{cost.notes || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-right text-slate-800 font-medium tabular-nums">{hidden ? masked : fmtCurrency(cost.amount)}</td>
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>

      {showAdd && (
        <div className="mt-3">
          <QuickAddCostRow
            newLabel={newLabel}
            newAmount={newAmount}
            saving={addSaving}
            onLabel={setNewLabel}
            onAmount={setNewAmount}
            onSave={handleAddCost}
            onCancel={() => { setShowAdd(false); setNewLabel(""); setNewAmount(""); }}
          />
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
        <div>
          <span className="text-slate-400">Total Costs</span>
          <p className="font-semibold text-slate-800 mt-0.5">{hidden ? masked : fmtCurrency(total)}</p>
        </div>
        <div className="text-right">
          <span className="text-slate-400">Profit</span>
          <p className={`font-semibold mt-0.5 ${profit == null ? "text-slate-300" : profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {profit == null ? "—" : hidden ? masked : fmtCurrency(profit)}
          </p>
        </div>
      </div>
    </section>
  );
}

export function ProjectFinancialSection({ project, onSave, onRefresh }) {
  return (
    <>
      <FinancialSummary project={project} onSave={onSave} onRefresh={onRefresh} />
      <CostsCard project={project} onSave={onSave} />
    </>
  );
}
